package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.ApiErrorBody
import dev.lingvo.shared.CreateShotReportShareRequest
import dev.lingvo.shared.CreatedShotReportShareDto
import dev.lingvo.shared.ShotAuditReportDto
import dev.lingvo.shared.ShotNotificationPreferencesDto
import dev.lingvo.shared.ShotNotificationWorkspacePreferencesDto
import dev.lingvo.shared.ShotReportShareDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.i18n.tr
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import dev.lingvo.site.i18n.t

/** Scoped transport bounds responses, aborts retired reads and never automatically repeats mutations. */
internal object ScreenshotUpdatesApi {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }

    suspend fun report(slug: String, platform: StorePlatform): ShotAuditReportDto =
        json.decodeFromString(send("GET", reportBase(slug, platform)))
    suspend fun shares(slug: String, platform: StorePlatform): List<ShotReportShareDto> =
        json.decodeFromString(send("GET", "${reportBase(slug, platform)}/shares"))
    suspend fun createShare(slug: String, platform: StorePlatform, days: Int): CreatedShotReportShareDto =
        json.decodeFromString(send("POST", "${reportBase(slug, platform)}/shares", json.encodeToString(CreateShotReportShareRequest(days))))
    suspend fun revokeShare(slug: String, platform: StorePlatform, id: String) {
        send("DELETE", "${reportBase(slug, platform)}/shares/${encodeURIComponent(id)}")
    }
    suspend fun personal(orgSlug: String): ShotNotificationPreferencesDto =
        json.decodeFromString(send("GET", "${preferencesBase(orgSlug)}/me"))
    suspend fun savePersonal(orgSlug: String, value: ShotNotificationPreferencesDto): ShotNotificationPreferencesDto =
        json.decodeFromString(send("PUT", "${preferencesBase(orgSlug)}/me", json.encodeToString(value)))
    suspend fun workspace(orgSlug: String): ShotNotificationWorkspacePreferencesDto =
        json.decodeFromString(send("GET", "${preferencesBase(orgSlug)}/workspace"))
    suspend fun saveWorkspace(orgSlug: String, value: ShotNotificationWorkspacePreferencesDto): ShotNotificationWorkspacePreferencesDto =
        json.decodeFromString(send("PUT", "${preferencesBase(orgSlug)}/workspace", json.encodeToString(value)))

    private fun reportBase(slug: String, platform: StorePlatform) =
        "/projects/${encodeURIComponent(slug)}/screenshots/${platform.id}/audit-report"
    private fun preferencesBase(slug: String) = "/orgs/${encodeURIComponent(slug)}/screenshot-notifications"

    private suspend fun send(method: String, path: String, body: String? = null): String {
        val controller: dynamic = js("typeof AbortController === 'undefined' ? null : new AbortController()")
        try {
            return withTimeout(20_000) {
                val headers = Headers().apply {
                    set("Accept", "application/json")
                    if (body != null) set("Content-Type", "application/json")
                    Session.token?.takeIf { it.isNotBlank() }?.let { set("Authorization", "Bearer $it") }
                }
                val init = RequestInit(method = method, headers = headers, body = body)
                if (controller != null) init.asDynamic().signal = controller.signal
                val response = window.fetch("${ApiConfig.baseUrl}$path", init).await()
                val raw = response.text().await()
                if (raw.length > 262144) throw ApiException(502, "invalid_response", requestFailed())
                if (!response.ok) {
                    val code = runCatching { json.decodeFromString<ApiErrorBody>(raw).error.code }.getOrNull()
                    throw ApiException(response.status.toInt(), code ?: "screenshot_updates_failed", requestFailed())
                }
                raw
            }
        } catch (_: TimeoutCancellationException) {
            throw ApiException(0, "request_timeout", requestFailed())
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (failure: ApiException) { throw failure }
        catch (_: Throwable) { throw ApiException(0, "screenshot_updates_failed", requestFailed()) }
        finally { controller?.abort() }
    }

    private fun requestFailed() = tr("app.projects.slug.screenshots.updates_api.couldnt_complete_this_request_check_your", "Couldn't complete this request. Check your connection and try again.")
}

private external fun encodeURIComponent(value: String): String
